import React, { useState, useContext, useCallback, useLayoutEffect } from 'react';
import _ from 'lodash';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@material-ui/core';

import './SportTable.scss';
import SportsBettingContext from '../Context/SportsBetting.context';
import SportContext, { SportContextProps } from '../Context/Sport.context';
import { ListSport } from '../../../interfaces/EventsState';
import MainMarkets from '../MainMarkets/MainMarkets';
import TournamentTable from '../TournamentTable/TournamentTable';
import useIsMobile from 'src/hooks/useIsMobile';

interface SportTableProps {
  sport: ListSport
}

function SportTable({ sport }: SportTableProps) {
  const [selectedMainMarkets, setSelectedMainMarkets] = useState<SportContextProps['selectedMainMarkets']>([]);
  const { maxMainMarkets } = useContext(SportsBettingContext);
  const mobile = useIsMobile();

  const context: SportContextProps = {
    selectedMainMarkets,
    screen: mobile ? 'mobile' : 'desktop',
    sport
  };

  useLayoutEffect(() => {
    if (sport.mainMarkets && !_.isEmpty(sport.mainMarkets) && (selectedMainMarkets.length === 0 || selectedMainMarkets.length !== maxMainMarkets)) {
      let newSelectedMainMarkets = [];
      const markets = Object.values(sport.mainMarkets);

      for (let i = 0; i < maxMainMarkets; i++) {
        if (!markets[i]) {
          break;
        }

        newSelectedMainMarkets.push({
          column: i,
          ...markets[i] as {}
        });
      }

      newSelectedMainMarkets = _.sortBy(newSelectedMainMarkets, 'column');

      setSelectedMainMarkets(newSelectedMainMarkets);
    }
  }, [maxMainMarkets, sport.mainMarkets]);

  const selectMainMarket = useCallback((index, marketTypeCode) => (event) => {
    event.preventDefault();
    const mainMarket = _.find(sport.mainMarkets, { marketTypeCode: event.target.value });

    let newSelectedMainMarkets = JSON.parse(JSON.stringify(selectedMainMarkets));
    _.pullAt(newSelectedMainMarkets, _.findIndex(newSelectedMainMarkets, { marketTypeCode }));

    newSelectedMainMarkets.push({
      column: index,
      index: event.target.value,
      ...mainMarket as {}
    });

    newSelectedMainMarkets = _.sortBy(newSelectedMainMarkets, 'column');
    setSelectedMainMarkets(newSelectedMainMarkets);
  }, [selectedMainMarkets, sport.mainMarkets]);

  return (
    <SportContext.Provider value={ context }>
      <div className="sport-table">
        <div className="sport-table__tournament-table">
          <Table padding="none" cellSpacing="0">
            <TableHead>
              <TableRow className="sport-table__sport-header">
                <TableCell align="center" style={ { width: '45px' } }>
                  <div className="sport-table__sport-icon sport-icon">{ sport.icon ? sport.icon : sport.sportIcon }</div>
                </TableCell>

                <TableCell align="left" colSpan={ 2 } className="sport-table__table-cell-name">
                  <div className="sport-table__sport-name" title={ sport.name }>{ sport.name }</div>
                </TableCell>
                <TableCell align="center" style={ { width: '45px' } } />
                { !mobile && <TableCell align="center" style={ { width: '45px' } } /> }
                {
                  _.map(selectedMainMarkets, (mainMarket, mi) => (
                    <MainMarkets
                      key={ mainMarket.marketTypeCode }
                      index={ mi }
                      selectedMarket={ mainMarket }
                      onChange={ selectMainMarket }
                    />
                  ))
                }

                {
                  !mobile && (
                    <>
                      <TableCell align="center" style={ { width: '45px' } }>
                        <div className="sport-table__event-markets sport-table__event-markets--clear"></div>
                      </TableCell>

                      <TableCell align="center" style={ { width: '30px' } }></TableCell>
                    </>
                  )
                }
              </TableRow>
            </TableHead>

            <TableBody>
              {
                _.map(sport.tournaments, (tournament, index) => (
                  <TournamentTable key={ `s:${ sport.code }_t:${ tournament.code }_${ index }` } tournament={ tournament } />
                ))
              }
            </TableBody>
          </Table>
        </div>
      </div>
    </SportContext.Provider>
  );
}

export default React.memo(SportTable);
